'use client';

import { useTranslation } from 'react-i18next';
import { useMediaSource, type ChannelDetail } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { SubscribeButton } from '../islands/SubscribeButton';
import { VideoRail } from './VideoRail';
import { sectionPad, sectionInner } from '../_shared/styles';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * /video/channels/[slug] — channel detail. Banner + avatar + subscribe
 * + videos rail + series rail.
 */
export interface ChannelDetailLayoutProps {
  slug: string;
  videoHrefTemplate?: string;
  seriesHrefTemplate?: string;
}

function compactCount(n: number | undefined): string {
  if (typeof n !== 'number') return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function ChannelDetailLayout({
  slug,
  videoHrefTemplate = '/tube/{slug}',
  seriesHrefTemplate = '/tube/series/{slug}',
}: ChannelDetailLayoutProps) {
  const { t } = useTranslation('site-builder-blocks');
  const media = useMediaSource();
  const state = useAsyncResource<ChannelDetail>(
    async (signal) => {
      if (!media) return null;
      return await media.channels.fetchBySlug(slug, signal);
    },
    [media, slug],
  );

  if (!media) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="ChannelDetailLayout" adapterName="MediaSource" />
        </div>
      </BlockRoot>
    );
  }
  if (state.kind !== 'loaded') return null;
  const c = state.data;

  const metaLine = [
    c.subscriberCount !== undefined
      ? t('channelDetail.subscribers', { subscribers: compactCount(c.subscriberCount) })
      : null,
    c.videoCount !== undefined ? t('channelDetail.videoCount', { count: c.videoCount }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <BlockRoot as="section" style={sectionPad}>
      <div style={sectionInner}>
        {c.bannerUrl && (
          <div
            aria-hidden
            style={{
              aspectRatio: '6 / 1',
              background: `url(${c.bannerUrl}) center/cover no-repeat`,
              borderRadius: 'var(--radius-lg, 16px)',
              marginBottom: 16,
            }}
          />
        )}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-5, 20px)',
            flexWrap: 'wrap',
            background: 'var(--color-surface-card, #fff)',
            border: '1px solid var(--color-border-default, rgba(0,0,0,0.08))',
            borderRadius: 'var(--radius-lg, 16px)',
            boxShadow: 'var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.04))',
            padding: 'var(--space-5, 20px)',
            marginBottom: 28,
          }}
        >
          <div
            aria-hidden
            style={{
              width: 88,
              height: 88,
              borderRadius: '50%',
              background: c.avatarUrl
                ? `url(${c.avatarUrl}) center/cover no-repeat`
                : 'var(--color-surface-muted, #e4e4e7)',
              flexShrink: 0,
              boxShadow: 'inset 0 0 0 1px var(--color-border-default, rgba(0,0,0,0.06))',
            }}
          />
          <div style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'clamp(22px, 3vw, 30px)',
                fontWeight: 700,
                color: 'var(--color-text-primary, #18181b)',
              }}
            >
              {c.name}
            </h1>
            {metaLine && (
              <div style={{ fontSize: '13px', color: 'var(--color-text-tertiary, #9ca3af)' }}>
                {metaLine}
              </div>
            )}
            {c.description && (
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: '14px',
                  lineHeight: 1.5,
                  color: 'var(--color-text-secondary, #71717a)',
                  maxWidth: 720,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical' as const,
                  overflow: 'hidden',
                }}
              >
                {c.description}
              </p>
            )}
          </div>
          <SubscribeButton channelSlug={c.slug} />
        </header>

        {c.videos && c.videos.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <VideoRail
              title={t('shared.label.videos')}
              items={c.videos}
              videoHrefTemplate={videoHrefTemplate}
            />
          </div>
        )}
        {c.series && c.series.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <VideoRail
              title={t('shared.label.series')}
              items={c.series}
              variant="poster"
              videoHrefTemplate={seriesHrefTemplate}
            />
          </div>
        )}
      </div>
    </BlockRoot>
  );
}
